import * as React from 'react';
import { inject, observer } from 'mobx-react';
import { Button, Modal, Checkbox } from 'components/antd';
import { NewBookingsStore, RootStore } from 'AppClient/stores';
import { CancellationPolicyTCText } from '../TermsAndConditionsText/CancellationPolicyTCText';
import { WaitingTimeTCText } from '../TermsAndConditionsText/WaitingTimeTCText';
import { BankHolidayTCText } from '../TermsAndConditionsText/BankHolidayTCText';
import { AdditionalChargesTCText } from '../TermsAndConditionsText/AdditionalChargesTCText';
import { GeneralTCText } from '../TermsAndConditionsText/GeneralTCText';
import { TermsAndConditions } from 'AppClient/api/Bookings/BokkingsTypes';
import Alert from 'components/antd/Alert/Alert';
import { isNil } from 'lodash-es';
import classNames from 'classnames';
import { ThemeContext } from 'app-themes';
import styles from './styles.module.scss';

interface Props {
	newBookingsStore?: NewBookingsStore;
}

@inject(({ newBookingsStore }: RootStore) => ({
	newBookingsStore,
}))
@observer
export class TermsAndConditionsModal extends React.Component<Props> {
	static contextType = ThemeContext;

	toggleModal = () => {
		this.props.newBookingsStore!.toggleTermsAndConditionsModal();
	};

	get getModalTitle() {
		const { isTermsAndConditionsAcceptModalType } = this.props.newBookingsStore!;

		return isTermsAndConditionsAcceptModalType ? (
			<span>Create new booking</span>
		) : (
			<span>Terms &#38; Conditions</span>
		);
	}

	get footerButton() {
		const {
			isTermsAndConditionsAcceptModalType,
			acceptBookingById,
			isLoadingCreateUpdate,
		} = this.props.newBookingsStore!;

		return (
			<div className={styles.footerInner}>
				{this.showNotice()}
				{this.showConfirmation()}
				<div className={styles.footerButtonWrapper}>
					<Button
						size="large"
						disabled={isLoadingCreateUpdate}
						onClick={this.toggleModal}
					>
						{isTermsAndConditionsAcceptModalType ? 'Cancel' : 'Close'}
					</Button>
					{isTermsAndConditionsAcceptModalType && (
						<Button
							size="large"
							disabled={isLoadingCreateUpdate || this.isDisabled()}
							type="primary"
							key="action"
							onClick={acceptBookingById}
						>
							Accept &#38; Book
						</Button>
					)}
				</div>
			</div>
		);
	}

	get getSubTitle() {
		const {
			isTermsAndConditionsAcceptModalType,
			termsAndConditionsModalData,
		} = this.props.newBookingsStore!;
		const theme = this.context;

		return (
			<>
				{isTermsAndConditionsAcceptModalType ? (
					<div className={styles.termsTitle}>Service partner terms &#38; conditions</div>
				) : (
					<div
						className={classNames(styles.termsSubtitle, {
							[styles[`termsSubtitle-${theme}`]]: theme !== '',
						})}
					>
						{termsAndConditionsModalData.servicePartnerName}
					</div>
				)}
			</>
		);
	}

	getData<T>(key: keyof TermsAndConditions): T | null {
		const { termsAndConditionsModalData } = this.props.newBookingsStore!;
		const { termsAndConditionsData } = termsAndConditionsModalData;

		if (termsAndConditionsData && termsAndConditionsData[key]) {
			return (termsAndConditionsData[key] as unknown) as T;
		}

		return null;
	}

	showNotice = () => {
		const { termsAndConditionsModalData } = this.props.newBookingsStore!;

		const { termsAndConditionsData } = termsAndConditionsModalData;

		if (termsAndConditionsData && termsAndConditionsData.isPickUpTimeChanged) {
			return (
				<div className={styles.footerAlertWrapper}>
					<Alert
						type="info"
						message={`This service partner will track the flight and may adjust your pick-up time to
				arrive at the airport in time. You will be notified about any updates.`}
						showIcon
						isStatic
						className={styles.footerAlert}
					/>
				</div>
			);
		}
		return null;
	};

	showConfirmation = () => {
		const {
			confirmationPickUpTimeChanged,
			toggleConfirmationPickUpTimeChanged,
			isTermsAndConditionsAcceptModalType,
			termsAndConditionsModalData,
			isLoadingCreateUpdate,
		} = this.props.newBookingsStore!;

		const { termsAndConditionsData } = termsAndConditionsModalData;

		if (
			termsAndConditionsData &&
			termsAndConditionsData.isPickUpTimeChanged &&
			isTermsAndConditionsAcceptModalType
		) {
			return (
				<div className={styles.footerCheckboxWrapper}>
					<Checkbox
						checked={confirmationPickUpTimeChanged}
						onChange={toggleConfirmationPickUpTimeChanged}
						disabled={isLoadingCreateUpdate}
					>
						I accept FlygTaxi may change my pick-up time according to traffic, to ensure
						I am on time for my flight
					</Checkbox>
				</div>
			);
		}

		return null;
	};

	isDisabled = () => {
		const {
			confirmationPickUpTimeChanged,
			termsAndConditionsModalData,
		} = this.props.newBookingsStore!;

		const { termsAndConditionsData } = termsAndConditionsModalData;

		if (termsAndConditionsData && termsAndConditionsData.isPickUpTimeChanged) {
			return !confirmationPickUpTimeChanged;
		}

		return false;
	};

	render() {
		const {
			isLoadingCreateUpdate,
			isShownTermsAndConditionsModal: visible,
			termsAndConditionsModalData,
			bookBookingData,
		} = this.props.newBookingsStore!;

		const {
			termsAndConditionsData,
			serviceProviderCurrencySymbol,
		} = termsAndConditionsModalData;

		const { customerCurrencySymbol } = bookBookingData;

		if (isNil(termsAndConditionsData)) {
			return null;
		}

		return (
			<div>
				{visible ? (
					<Modal
						visible={visible}
						onCancel={this.toggleModal}
						title={this.getModalTitle}
						footer={this.footerButton}
						isLoading={isLoadingCreateUpdate}
						fixedFooter={termsAndConditionsData.isPickUpTimeChanged}
					>
						{this.getSubTitle}
						<div className="modalBox modalBoxBooking">
							<div className="modalBoxRow">
								<div className="modalBoxCol">
									<div className={styles.terms}>
										<div className={styles.termsBlock}>
											<CancellationPolicyTCText
												data={this.getData('cancellationPolicy')}
												customerCurrencySymbol={customerCurrencySymbol}
											/>
										</div>
										<div className={styles.termsBlock}>
											<WaitingTimeTCText
												data={this.getData('waitingTime')}
												serviceProviderCurrencySymbol={
													serviceProviderCurrencySymbol
												}
											/>
										</div>
										<div className={styles.termsBlock}>
											<BankHolidayTCText
												data={this.getData('bankHolidayUplift')}
											/>
										</div>
										<div className={styles.termsBlock}>
											<AdditionalChargesTCText
												data={this.getData('additionalCharges')}
												serviceProviderCurrencySymbol={
													serviceProviderCurrencySymbol
												}
											/>
										</div>
										<div className={styles.termsBlock}>
											<GeneralTCText
												data={this.getData('quoteContainsVat')}
											/>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Modal>
				) : null}
			</div>
		);
	}
}
